import Joi from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getColleges,
  getPageBlocks,
  getUserTypes,
  getDepartments,
  getNotificationTypes,
  getUserPageBlocks,
  getCollegeDepartments,
} from "../../../../../action/common/commonActionCreator";
import { createNotification } from "../../../../../action/masters/notificationActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ICreateNotification,
  INotificationData,
} from "../../../../../models/master/notification";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import { IDropdownData } from "../../../../../models/ICommon";
import { pageBlockId } from "../../../../../constants/pageBlocks";
import LoaderSpinner from "../../../../../common-components/loader";
import { masterType } from "../../../../../constants/masterTypes";
import {
  booleanOptions,
  maxImageSize,
} from "../../../../../constants/commonConstants";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";

const NotificationCreate = (props: ICreateNotification) => {
  const {
    createNotification,
    setShowCreate,
    getColleges,
    currentUser,
    colleges,
    getPageBlocks,
    pageBlocks,
    getUserTypes,
    getDepartments,
    departments,
    userTypes,
    getNotificationTypes,
    notificationTypes,
    getAllNotifications,
    getUserPageBlocks,
    getCollegeDepartments,
    collegeDepartments,
  } = props;
  const navigate = useNavigate();

  const initialData: INotificationData = {
    id: null,
    userType: currentUser === UserTypes.DepartmentAdmin ? UserTypes.Staff : 0,
    title: "",
    file: null,
    notificationMessage: "",
    isActive: true,
    priority: 0,
    startDate: "",
    endDate: "",
    notificatonTypeId: 0,
    departmentIds: [],
    pageBlockIds: [],
    collegeIds: [],
    isViewed: false,
    createdOn: "",
    isFreshman: false,
  };

  // states

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [pageblock, setPageblock] = useState<IDropdownData[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const freshmanOptionsList = Object.entries(booleanOptions)?.map(
    ([key, value]) => ({
      id: value,
      name: key,
    })
  );

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (currentUser === UserTypes.DepartmentAdmin) {
      _data.collegeIds = colleges?.map((college) => college.id);
      _data.departmentIds = departments?.map((department) => department.id);
    }

    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value), masterType.notification);

      if (Number(event.target.value) === UserTypes.Staff) {
        const fixedPageBlock = pageBlocks?.find(
          (block) => block.id === pageBlockId.staffNotification
        );
        _data.pageBlockIds = [fixedPageBlock?.id || 0];
      } else if (Number(event.target.value) === UserTypes.Faculty) {
        const fixedPageBlock = pageBlocks?.find(
          (block) => block.id === pageBlockId.facultyNotification
        );
        _data.pageBlockIds = [fixedPageBlock?.id || 0];
      } else if (Number(event.target.value) === UserTypes.Student) {
        const fixedPageBlock = pageBlocks?.find(
          (block) => block.id === pageBlockId.studentNotification
        );
        _data.pageBlockIds = [fixedPageBlock?.id || 0];
      } else {
        _data.collegeIds = [];
        _data.departmentIds = [];
        _data.pageBlockIds = [];
      }
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  const onFileRemove = () => {
    setData({ ...data, file: null });
    setImagePreview(null);
    setImageName(null);
    if (errors?.file) {
      const newErrors = { ...errors };
      delete newErrors.file;
      setErrors(newErrors);
    }
  };

  const schema = {
    title: Joi.string().required().label("Title is required"),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    startDate: Joi.string().required().label("Start Date is required"),
    endDate: Joi.string().required().label("End Date is required"),
    notificationMessage: Joi.string()
      .required()
      .label("Notification Message is required"),
    notificatonTypeId: Joi.number()
      .greater(0)
      .required()
      .label("Select Notification Type"),
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select University Management"),
    isFreshman: Joi.boolean()
      .allow("", null)
      .optional()
      .label("Freshman selection is required"),
  };

  const schemaForSuperAdmin = {
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select University Management"),
    departmentIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Department"),
  };

  const onSave = async () => {
    let _schema =
      currentUser == UserTypes.SuperAdmin
        ? Joi.object().keys({ ...schema, ...schemaForSuperAdmin })
        : Joi.object().keys({ ...schema });
    const validationErrors = validateFields(data, _schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("userType", data.userType?.toString() || "");
      formData.append("title", data.title);
      formData.append("file", data.file);
      formData.append("isFreshman", data.isFreshman.toString());
      formData.append(
        "notificationMessage",
        data.notificationMessage.replace(/\s+$/g, "")
      );
      formData.append(
        "notificatonTypeId",
        data.notificatonTypeId.toString() || ""
      );
      formData.append("isActive", data.isActive.toString());
      formData.append("priority", data.priority.toString());
      formData.append("startDate", data.startDate);
      formData.append("endDate", data.endDate);
      data.pageBlockIds.map((item, index) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      data.collegeIds.map((item, index) => {
        formData.append(`collegeIds[${index}]`, item.toString());
      });
      data.departmentIds.map((item, index) => {
        formData.append(`departmentIds[${index}]`, item.toString());
      });

      setLoading(true);
      setIsDisabled(true);
      try {
        await createNotification(formData, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllNotifications();
      setShowCreate(false);
    }
  };

  //setting warning alert on cancel
  const onCancel = async () => {
    setShowAlertCancel(true);
    setAlert(alertConstants.cancelCheck);
  };

  useEffect(() => {
    getColleges();
    getUserTypes();
    getDepartments();
    getNotificationTypes();

    if (!UserTypes.DepartmentAdmin) {
      getPageBlocks();
    }
  }, []);

  useEffect(() => {
    if (colleges && colleges.length > 0) {
      setData((prevData) => ({
        ...prevData,
        collegeIds: colleges.map((college) => college.id),
      }));
    }
  }, [colleges]);

  useEffect(() => {
    if (UserTypes.DepartmentAdmin) {
      getUserPageBlocks(UserTypes.Staff, masterType.notification);
    }
  }, []);

  useEffect(() => {
    let fixedPageBlock: any;

    if (data.userType === UserTypes.Staff) {
      fixedPageBlock = pageBlocks?.find(
        (block) => block.id === pageBlockId.staffNotification
      );
      setPageblock(fixedPageBlock ? [fixedPageBlock] : []);
      setData((prevData) => ({
        ...prevData,
        pageBlockIds: [fixedPageBlock?.id || 0],
      }));
    } else if (data.userType === UserTypes.Faculty) {
      fixedPageBlock = pageBlocks?.find(
        (block) => block.id === pageBlockId.facultyNotification
      );
      setPageblock(fixedPageBlock ? [fixedPageBlock] : []);
      setData((prevData) => ({
        ...prevData,
        pageBlockIds: [fixedPageBlock?.id || 0],
      }));
    } else if (data.userType === UserTypes.Student) {
      fixedPageBlock = pageBlocks?.find(
        (block) => block.id === pageBlockId.studentNotification
      );
      setPageblock(fixedPageBlock ? [fixedPageBlock] : []);
      setData((prevData) => ({
        ...prevData,
        pageBlockIds: [fixedPageBlock?.id || 0],
      }));
    }
  }, [data.userType, pageBlocks]);

  useEffect(() => {
    if (data.collegeIds?.length > 0) {
      getCollegeDepartments(data.collegeIds.toString());
    } else {
      setData((prevData) => ({
        ...prevData,
        departmentIds: [],
      }));
    }
  }, [data.collegeIds]);

  useEffect(() => {
    if (
      collegeDepartments &&
      data.collegeIds &&
      collegeDepartments.length > 0 &&
      data.collegeIds.length > 0
    ) {
      setData((prevData) => ({
        ...prevData,
        departmentIds: collegeDepartments?.map((dept) => dept.id),
      }));
    }
  }, [collegeDepartments, data.collegeIds]);

  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    errors && delete errors[name];
    const updatedData = { ...data, [name]: value };

    // Validate dates
    if (name === "endDate" && value < updatedData.startDate) {
      setErrors({
        ...errors,
        endDate: "End date cannot be earlier than the start date.",
      });
    } else if (
      name === "startDate" &&
      updatedData.endDate &&
      updatedData.endDate < value
    ) {
      setErrors({ ...errors, endDate: "Please adjust the end date." });
      updatedData.endDate = "";
    }
    setData(updatedData);
  };

  return (
    <>
      <div className="modal-header">
        <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
          Add New Notification
        </h1>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={() => setShowCreate(false)}
        ></button>
      </div>
      <div className="modal-body">
        <div className="row">
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"Notification Type "}
              onChange={onChange}
              isMandatory={true}
              value={data.notificatonTypeId}
              name={"notificatonTypeId"}
              optionsList={sortAlphabetically(notificationTypes)}
            />
            {errors?.notificatonTypeId && (
              <div className="text-danger">{errors.notificatonTypeId}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"User Type "}
              onChange={onChange}
              isMandatory={true}
              disable={currentUser == UserTypes.DepartmentAdmin ? true : false}
              value={data.userType}
              name={"userType"}
              optionsList={sortAlphabetically(
                userTypes?.filter((type) => type.id !== UserTypes.Freshman)
              )}
            />
            {errors?.userType && (
              <div className="text-danger">{errors.userType}</div>
            )}
          </div>
          {data.userType === UserTypes.Student && (
            <div className="col-lg-12 form-group">
              <FloatingLabelDropDown
                label={"Is Freshman"}
                onChange={onChange}
                isMandatory={true}
                value={data.isFreshman}
                name={"isFreshman"}
                optionsList={freshmanOptionsList}
              />
              {errors?.freshman && (
                <div className="text-danger">{errors.freshman}</div>
              )}
            </div>
          )}
          <div className="col-md-6 form-group">
            <FloatingLabelTextInput
              label={"Title"}
              onChange={onChange}
              isMandatory={true}
              value={data.title}
              name={"title"}
              maxLength={50}
            />
            {errors?.title && <div className="text-danger">{errors.title}</div>}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">Image</label>
            <div className="inputfile-flex">
              {imagePreview && (
                <div className="inputfile-img">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: "50px", height: "50px" }}
                  />
                </div>
              )}
              <div className="inputfile-wrap">
                <input
                  type="file"
                  name="file"
                  id="image"
                  className="form-control d-none"
                  required
                  onChange={onFileChange}
                  accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp, image/x-icon"
                  value={errors?.file && ""}
                />
                <div className="inputfile">
                  <label htmlFor="image">Choose File</label>
                  <span>
                    {imageName
                      ? imageName.trim().split("/").pop()
                      : "No File Choosen"}
                  </span>
                  {imagePreview && (
                    <button
                      type="button"
                      className="btn-close"
                      onClick={onFileRemove}
                      title="Remove image"
                    ></button>
                  )}
                </div>
              </div>
            </div>
            {errors?.file && <div className="text-danger">{errors.file}</div>}
          </div>
          <div className="col-md-12 form-group">
            <label className="profile_details_text">
              Notification Message<i className="label-asterisk"> * </i>
            </label>
            <textarea
              name="notificationMessage"
              value={data.notificationMessage}
              onChange={onChange}
              maxLength={1000}
            ></textarea>
            {errors?.notificationMessage && (
              <div className="text-danger">{errors.notificationMessage}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">
              Start Date<i className="label-asterisk"> * </i>
            </label>
            <input
              type="date"
              name="startDate"
              className="form-control"
              onChange={onChangeDate}
              value={data.startDate}
              onKeyDown={(e) => e.preventDefault()}
              inputMode="none"
              min={new Date().toISOString().split("T")[0]}
            />
            {errors?.startDate && (
              <div className="text-danger">{errors.startDate}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">
              End Date<i className="label-asterisk"> * </i>
            </label>
            <input
              type="date"
              name="endDate"
              className="form-control"
              onChange={onChangeDate}
              value={data.endDate}
              onKeyDown={(e) => e.preventDefault()}
              inputMode="none"
              min={
                data.startDate
                  ? data.startDate
                  : new Date().toISOString().split("T")[0]
              }
            />
            {errors?.endDate && (
              <div className="text-danger">{errors.endDate}</div>
            )}
          </div>
          {currentUser === UserTypes.SuperAdmin && (
            <div className="col-md-6 form-group">
              <MultipleSelect
                label={"Departments"}
                onChange={onChange}
                isMandatory={true}
                values={data.departmentIds as number[]}
                name={"departmentIds"}
                optionList={sortAlphabetically(collegeDepartments)}
                disabled={data.collegeIds.length === 0}
              />
              {errors?.departmentIds && (
                <div className="text-danger">{errors.departmentIds}</div>
              )}
            </div>
          )}

          <div
            className={`col-md-${
              currentUser === UserTypes.DepartmentAdmin ? 12 : 6
            } form-group`}
          >
            <FloatingLabelTextInput
              label={"Page-Block "}
              onChange={onChange}
              isMandatory={false}
              value={
                pageBlocks?.find((p) => p.id == data.pageBlockIds[0])?.name
              }
              name={"pageBlockIds"}
              disable
            />
          </div>

          {currentUser === UserTypes.SuperAdmin && (
            <div className="col-md-12 form-group">
              <MultipleSelect
                label={"University Management"}
                onChange={onChange}
                isMandatory={true}
                values={data.collegeIds as number[]}
                name={"collegeIds"}
                optionList={sortAlphabetically(colleges)}
              />
              {errors?.collegeIds && (
                <div className="text-danger">{errors.collegeIds}</div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={isDisabled}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            data-bs-dismiss="modal"
            onClick={() => setShowCreate(false)}
            disabled={isDisabled}
          >
            Close
          </button>
        </div>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {loading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  createNotification: (data: any, callBack: Function) =>
    createNotification(data, callBack),
  getColleges: () => getColleges(),
  getPageBlocks: () => getPageBlocks(),
  getUserTypes: () => getUserTypes(),
  getDepartments: () => getDepartments(),
  getNotificationTypes: () => getNotificationTypes(),
  getUserPageBlocks: (userType: number, masterType: string) =>
    getUserPageBlocks(userType, masterType),
  getCollegeDepartments: (collegeIds: string) =>
    getCollegeDepartments(collegeIds),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
    departments: state.common.departments,
    notificationTypes: state.common.notificationTypes,
    collegeDepartments: state.common.collegeDepartments,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NotificationCreate);
